import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { BLACK_60, FONT_COLOR } from '../constants/colors';
import checkImg from '../../assets/icons/check.svg';

const HiddenInput = styled.input`
  display: none;
`;

const Check = styled.div<{ $checked: boolean; $color: string; $readOnly?: boolean }>`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 6px;
  box-sizing: border-box;

  ${({ $checked, $color }) =>
    $checked
      ? css`
          border: 2px solid transparent;
          background-color: ${$color};
          background-image: url(${checkImg});
          background-position: center;
          background-repeat: no-repeat;
        `
      : css`
          border: 2px solid ${BLACK_60};
          background: #ffffff;
        `}

  ${({ $readOnly }) =>
    $readOnly &&
    css`
      opacity: 0.6;
    `}
`;

const RootLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const LabelText = styled.span`
  font-size: 16px;
  line-height: 22px;
  color: ${FONT_COLOR};
`;

export type CheckboxProps = {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  color?: string;
  readOnly?: boolean;
  children?: React.ReactNode;
  className?: string;
};

const Checkbox = ({
  checked,
  onChange,
  color = FONT_COLOR,
  readOnly = false,
  children,
  className,
}: React.PropsWithChildren<CheckboxProps>) => {
  const handleChange = useCallback(() => {
    if (readOnly) return;
    onChange?.(!checked);
  }, [checked, onChange, readOnly]);

  const control = (
    <>
      <HiddenInput type="checkbox" checked={checked} onChange={handleChange} disabled={readOnly} />
      <Check $checked={checked} $color={color} $readOnly={readOnly} />
    </>
  );

  if (children) {
    return (
      <RootLabel className={className}>
        {control}
        <LabelText>{children}</LabelText>
      </RootLabel>
    );
  }

  return <span className={className}>{control}</span>;
};

export default Checkbox;
